import React from "react";

/**
 * ScoreRing — adapts to any size.
 * size >= 160  → full (label + number + /100)
 * size >= 90   → compact (number + /100 only)
 * size < 90    → minimal (number only, clean)
 */
export default function ScoreRing({ value = 86, size = 280, dark = false }) {
  const compact = size < 160;
  const minimal = size < 90;

  const stroke = minimal ? 6 : compact ? 7 : 10;
  const gap = minimal ? 10 : compact ? 12 : 18;

  const r1 = size / 2 - stroke - 2;
  const r2 = minimal ? null : r1 - gap;
  const r3 = minimal ? null : (compact ? null : r2 - gap);

  const c1 = 2 * Math.PI * r1;
  const c2 = r2 ? 2 * Math.PI * r2 : 0;
  const c3 = r3 ? 2 * Math.PI * r3 : 0;

  // Use actual value for outer ring; inner rings are decorative
  const pct = Math.min(Math.max((value || 0) / 100, 0), 1);
  const v2 = 0.74;
  const v3 = 0.91;

  const track = dark ? "rgba(255,255,255,0.10)" : "rgba(28,25,23,0.08)";
  const textColor = dark ? "#FDFBF7" : "#1C1917";
  const sub = dark ? "rgba(253,251,247,0.6)" : "rgba(28,25,23,0.5)";

  const numSize = minimal ? size * 0.36 : compact ? size * 0.30 : size * 0.28;

  return (
    <div
      className="relative inline-flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={size >= 200 ? "wsh-rotate-slow" : ""}
        aria-hidden
      >
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {/* Outer ring — actual score */}
          <circle cx={size/2} cy={size/2} r={r1} fill="none" stroke={track} strokeWidth={stroke} />
          <circle
            cx={size/2} cy={size/2} r={r1}
            fill="none" stroke="#C2410C" strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={`${c1 * pct} ${c1}`}
          />

          {/* Middle ring — decorative (hidden in minimal) */}
          {r2 && (
            <>
              <circle cx={size/2} cy={size/2} r={r2} fill="none" stroke={track} strokeWidth={stroke} />
              <circle
                cx={size/2} cy={size/2} r={r2}
                fill="none" stroke="#15803D" strokeWidth={stroke} strokeLinecap="round"
                strokeDasharray={`${c2 * v2} ${c2}`}
              />
            </>
          )}

          {/* Inner ring — decorative (full size only) */}
          {r3 && (
            <>
              <circle cx={size/2} cy={size/2} r={r3} fill="none" stroke={track} strokeWidth={stroke} />
              <circle
                cx={size/2} cy={size/2} r={r3}
                fill="none" stroke="#D97706" strokeWidth={stroke} strokeLinecap="round"
                strokeDasharray={`${c3 * v3} ${c3}`}
              />
            </>
          )}
        </g>
      </svg>

      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        {!compact && (
          <span className="overline" style={{ color: sub, fontSize: 9, letterSpacing: "0.12em" }}>
            WISHIRE
          </span>
        )}
        <span
          className="font-display font-bold leading-none"
          style={{ color: textColor, fontSize: numSize }}
        >
          {value || 0}
        </span>
        {!minimal && (
          <span style={{ color: sub, fontSize: compact ? 9 : 11, letterSpacing: "0.15em", fontFamily: "monospace" }}>
            /100
          </span>
        )}
      </div>
    </div>
  );
}
